/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AclModel,
    L4BackendServiceModel,
    L4FrontendServiceModel,
    L4RealServiceModel,
    L7BackendServiceModel,
    L7RealServiceModel,
    L7VirtualServiceModel
} from '../../../models';
import { ErrorResponseModel } from '../../../../../../core/models';
import { Injectable } from '@angular/core';
import { L7HeaderModel } from '../../../models/l7.header.model';
import { Layer4ApiService, Layer7ApiService } from '../../../http';
import { LogService, NotifyService, UtilsService } from '../../../../../../core/services';
import { WizardControllerStateService } from './wizardControllerState.service';
import { WizardService } from './wizard.service';
import { WizardTitleEnum } from '../../../enums';
import { concatMap } from 'rxjs/operators';
import { forkJoin, from, Observable, pipe } from 'rxjs';

@Injectable()
export class Layer47SubmissionService {
    private apiService: any;

    constructor(
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private logService: LogService,
        private notifyService: NotifyService,
        private utilsService: UtilsService,
        private wizardControllerStateService: WizardControllerStateService,
        private wizardService: WizardService,
    ) {
    }

    /**
     * Set the api service depending on the template type.
     * @param type
     */
    public setApiServiceOnTemplateType(type: string) {
        this.apiService = type === 'layer4' ? this.layer4ApiService : this.layer7ApiService;
    }

    /**
     * Concat helper
     * @param controllerState
     */
    public concatMapHelper(controllerState: any): any {
        return pipe(
            concatMap(() => this.getHeadersFromControllerState(controllerState)),
            concatMap(() => this.submitService(controllerState, WizardTitleEnum.REALSERVERS)),
            concatMap(() => this.submitService(controllerState, WizardTitleEnum.BACKENDS)),
            concatMap(() => this.submitService(controllerState, WizardTitleEnum.ACLS)),
            concatMap(() => this.submitService(controllerState, WizardTitleEnum.FRONTENDS)),
            concatMap(() => this.submitService(controllerState, WizardTitleEnum.MONITORS))
        );
    }

    /**
     * Submit headers.
     * @param controllerState
     * @private
     */
    private getHeadersFromControllerState(controllerState: any): Observable<any> {
        return new Observable(observer => {
            const headers = controllerState[WizardTitleEnum.HEADERS];
            if (!headers || headers && !Object.keys(headers).length) {
                observer.next(false);
                observer.complete();

                return;
            } else {
                let counter: number = 1;
                from(Object.keys(headers) || []).pipe(
                    concatMap(service => this.formatHeaders(headers[service])))
                    .subscribe({
                        next: (): void => {
                            if (counter === Object.keys(headers).length) {
                                observer.next(true);
                                observer.complete();
                            }

                            counter++;
                        },
                        error: (error: ErrorResponseModel): void => {
                            const htmlDebugMessage = this.utilsService.formatCapturedError(error);
                            this.notifyService.debug(htmlDebugMessage, { static: true });
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        });
    }

    /**
     * Format headers object if they exist.
     * @param headers
     * @private
     */
    private formatHeaders(headers: any): Observable<any> {
        return new Observable(observer => {
            const noHeaders: boolean = !(headers.add?.length) && !(headers.update?.length) && !(headers.delete?.length);
            if (noHeaders) {
                observer.next(false);
                observer.complete();

                return;
            }

            const apiCalls: any = {};

            if (headers.add.length) {
                apiCalls['headerAdd'] = this.submitHeaders('add', headers.add);
            }

            if (headers.update.length) {
                apiCalls['headerUpdate'] = this.submitHeaders('update', headers.update);
            }

            if (headers.delete.length) {
                apiCalls['headerDelete'] = this.submitHeaders('delete', headers.delete);
            }

            // Make all the necessary api calls and wait for them to complete.
            forkJoin(apiCalls)
                .subscribe({
                    next: (): void => {
                        observer.next(true);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        const htmlDebugMessage = this.utilsService.formatCapturedError(error);
                        this.notifyService.debug(htmlDebugMessage, { static: true });
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Submit headers
     * @param headerType
     * @param headers
     * @private
     */
    private submitHeaders(headerType: string, headers: any): Observable<any> {
        return new Observable(observer => {
            let counter: number = 1;
            let tempId: string = '';
            from(headers || []).pipe(
                concatMap((header: L7HeaderModel) => {
                    tempId = header.id;

                    return this.headerApiCall(headerType, header);
                })
            ).subscribe({
                next: (data): void => {
                    if (data && headerType === 'add') {
                        const controllerState = this.wizardService.controllerState;
                        this.utilsService.createArrayIfNotExist(controllerState, WizardTitleEnum.SUCCESS);
                        this.utilsService.createArrayIfNotExist(controllerState[WizardTitleEnum.SUCCESS], WizardTitleEnum.HEADERS);
                        controllerState[WizardTitleEnum.SUCCESS][WizardTitleEnum.HEADERS].push({ tempId, id: data.id });
                    }

                    // Complete when all API calls have been made.
                    if (counter === headers.length) {
                        observer.next(true);
                        observer.complete();
                    }

                    counter++;
                },
                error: (error: ErrorResponseModel): void => {
                    const htmlDebugMessage = this.utilsService.formatCapturedError(error);
                    this.notifyService.debug(htmlDebugMessage, { static: true });
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Returns the correct API call for the header being submitted.
     * @param headerType
     * @param header
     * @private
     */
    private headerApiCall(headerType: any, header: any): Observable<any> {
        switch (headerType) {
            case 'add':
                return this.layer7ApiService.addHeader(header);
            case 'update':
                return this.layer7ApiService.editHeader(header.id, header);
            case 'delete':
                return this.layer7ApiService.deleteHeader(header);
        }
    }

    /**
     * Submits service from controllerState based on service name.
     * @param controllerState
     * @param serviceName
     * @private
     */
    private submitService(controllerState: any, serviceName: string): Observable<any> {
        return new Observable(observer => {
            if (!controllerState[serviceName] || !controllerState[serviceName].length) {
                observer.next(false);
                observer.complete();

                return;
            }

            let counter: number = 1;
            let tempId: string = '';
            const numberOfServices: number = controllerState[serviceName].length;

            from(controllerState[serviceName] || [])
                .pipe(
                    concatMap(serviceData => {
                        this.wizardService.wizardProgressMessage = `Submitting ${serviceName} ${counter} of ${numberOfServices}`;
                        const serviceValues = this.wizardControllerStateService.getValuesFromServiceData(controllerState, serviceData);
                        tempId = serviceValues.id;

                        if (!serviceValues || !Object.keys(serviceValues).length) {
                            return this.skip();
                        }

                        if (serviceValues['headerIds']) {
                            serviceValues['headerIds'] =
                                this.updateHeadersFromControllerState(controllerState, serviceValues['headerIds']);
                        }

                        this.updateControllerStates(serviceName, controllerState, serviceValues, counter);

                        return this.utilsService.isServiceId(serviceValues.id)
                            ? this.updateServiceApiCall(serviceName, serviceValues)
                            : this.createServiceApiCall(serviceName, serviceValues);
                    })
                )
                .subscribe({
                    next: (data: any): void => {
                        if (data) {
                            // Add our successful ID's to our success state store.
                            this.updateSuccessState(controllerState, data, tempId);

                            if (counter === numberOfServices) {
                                // Complete when all API calls have been made.
                                observer.next(data);
                                observer.complete();
                            }

                            counter++;
                        }
                    },
                    error: (error: ErrorResponseModel): void => {
                        error.extras = {
                            storedErrorId: tempId,
                            storedErrorIndex: counter - 1
                        };

                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Empty observable to allow the concatMap to continue even it the controller object is empty.
     * @private
     */
    private skip(): Observable<any> {
        return new Observable((observer) => {
                observer.next(true);
                observer.complete();
            }
        );
    }

    /**
     * Take the submit response and store our created service ID's
     * @param controllerState
     * @param data
     * @param tempId
     * @private
     */
    private updateSuccessState(controllerState: any, data: any, tempId: string): any {
        this.utilsService.createArrayIfNotExist(controllerState, WizardTitleEnum.SUCCESS);

        for (const i in data) {
            if (data.hasOwnProperty(i)) {
                switch (i) {
                    case 'vip':
                        this.setSuccessState(controllerState, WizardTitleEnum.FRONTENDS, data[i], tempId);
                        break;
                    case 'backend':
                        this.setSuccessState(controllerState, WizardTitleEnum.BACKENDS, data[i], tempId);
                        break;
                    case 'rip':
                        this.setSuccessState(controllerState, WizardTitleEnum.REALSERVERS, data[i], tempId);
                        break;
                    case 'acl':
                    case 'acls':
                        this.setSuccessState(controllerState, WizardTitleEnum.ACLS, data[i], tempId);
                        break;
                    default:
                        this.setSuccessState(controllerState, `${i}s`, data[i], tempId);
                        break;
                }

                return data[i];
            }
        }
        this.logService.debug('wizardControllerState:updateSuccessState()', 'Success state', controllerState[WizardTitleEnum.SUCCESS]);
    }

    /**
     * Update rollback state for a particular service type
     * @param controllerState
     * @param serviceType
     * @param data
     * @param tempId
     * @private
     */
    private setSuccessState(controllerState: any, serviceType: string, data: any, tempId: string): void {
        this.logService.debug('wizardControllerState:setSuccessState()', 'Setting success state for ' + serviceType, data);
        let id: any;

        if (Array.isArray(data)) {
            id = data.map(item => item.id);
        } else if (typeof data === 'object') {
            id = data?.id ? data.id : Object.values(data);
        } else {
            id = data;
        }

        this.wizardControllerStateService.pushIdToSuccessArray(controllerState, serviceType, id, tempId);
    }

    /**
     * Update headerIds from the controller state success ids.
     * @param controllerState
     * @param headerIds
     * @private
     */
    private updateHeadersFromControllerState(controllerState: any, headerIds: string[]): string[] {
        const submittedHeaders = this.utilsService.getObjectValue(controllerState[WizardTitleEnum.SUCCESS], WizardTitleEnum.HEADERS);
        if (submittedHeaders?.length && headerIds?.length) {
            const replacementHeaderIds: any[] = [];

            for (const id of headerIds) {
                if (id.startsWith('temp-')) {
                    const match: any = submittedHeaders.find((item: {
                        tempId: string,
                        id: string
                    }): boolean => item.tempId === id);
                    replacementHeaderIds.push(match.id);
                } else {
                    replacementHeaderIds.push(id); // include existing headers
                }
            }

            return replacementHeaderIds;
        }

        return headerIds;
    }

    /**
     * Update controller state for each service with success values.
     * @param serviceName
     * @param controllerState
     * @param serviceValues
     * @param counter
     * @private
     */
    private updateControllerStates(
        serviceName: string,
        controllerState: any,
        serviceValues: any,
        counter: number
    ): void {
        if (serviceName === WizardTitleEnum.BACKENDS) {
            this.updateBackend(controllerState, serviceValues, counter);
        }

        if (serviceName === WizardTitleEnum.ACLS) {
            this.updateAcl(controllerState, serviceValues);
        }

        if (serviceName === WizardTitleEnum.MONITORS) {
            this.updateMonitorAclIds(controllerState, serviceValues);
        }

        if (serviceName === WizardTitleEnum.FRONTENDS) {
            this.updateFrontendAclIds(serviceValues, controllerState, counter);
        }
    }

    /**
     * Update backend
     * @param controllerState
     * @param serviceValues
     * @param counter
     * @private
     */
    private updateBackend(controllerState: any, serviceValues: any, counter: number): void {
        const realServers: any[] = this.utilsService.getObjectValue(
            controllerState, `${[WizardTitleEnum.SUCCESS]}.${[WizardTitleEnum.REALSERVERS]}`
        );

        if (realServers?.length) {
            const realRipIds: string[] = [];
            if (!serviceValues.ripIds) {
                serviceValues['ripIds'] = [];
            }

            for (const ripId of serviceValues.ripIds) {
                if (!this.utilsService.isServiceId(ripId)) {
                    const match: any = realServers.find((item: any): boolean => item.tempId === ripId);
                    if (Array.isArray(match?.id)) {
                        const newArray: string[] = realRipIds.concat(match.id);
                        realRipIds.push(...newArray);
                    } else {
                        realRipIds.push(match.id);
                    }
                } else {
                    realRipIds.push(ripId);
                }
            }

            if (!realRipIds.length) {
                const ripsByIndex: string = this.fallBackToIndexes(realServers, counter);
                if (ripsByIndex) {
                    realRipIds.push(ripsByIndex);
                }
            }

            serviceValues.ripIds = realRipIds;
        }
    }

    /**
     * Fall back to using indexes for matching services.
     * @param services
     * @param counter
     * @private
     */
    private fallBackToIndexes(services: any[], counter: number): string {
        this.logService.debug('Layer47SubmissionService:fallBackToIndexes', 'Reverted to using indexes for matching services');

        return services[counter - 1].id;
    }

    /**
     * Update ACL in the controller state.
     * @param controllerState
     * @param serviceValues
     * @private
     */
    private updateAcl(controllerState: any, serviceValues: any): void {
        const successBackends: any = this.utilsService.getObjectValue(
            controllerState[WizardTitleEnum.SUCCESS], `${[WizardTitleEnum.BACKENDS]}`
        );

        if (successBackends?.length) {
            serviceValues.id = this.utilsService.isServiceId(serviceValues.id) ? serviceValues.id : '';
            if (serviceValues.redirectLocation?.includes('$')) {
                const index: number = serviceValues.redirectLocation.replace(/\D/g, '');
                serviceValues.redirectLocation = successBackends[index - 1].id;
            } else if (!serviceValues.redirectLocation && successBackends[0].id) {
                serviceValues.redirectLocation = successBackends[0].id;
            } else {
                serviceValues.redirectLocation = successBackends.find(
                    (item: any): boolean => item.tempId === serviceValues.redirectLocation
                )?.id;
            }
        }
    }

    /**
     * Update monitor ACL id.
     * @param controllerState
     * @param serviceValues
     * @private
     */
    private updateMonitorAclIds(controllerState: any, serviceValues: any): void {
        const aclTempIds: any = serviceValues.aclIds;
        const successAcls: any = this.utilsService.getObjectValue(controllerState[WizardTitleEnum.SUCCESS], `${[WizardTitleEnum.ACLS]}`);
        if (successAcls?.length) {
            const realAclIds: string[] = [];

            for (const tempId of aclTempIds) {
                if (tempId.startsWith('temp-')) {
                    realAclIds.push(successAcls.find((item: any): boolean => item.tempId === tempId).id);
                } else {
                    realAclIds.push(tempId);
                }
            }
            serviceValues.aclIds = realAclIds;
        }
    }

    /**
     * Update frontend ACL ID.
     * @param serviceValues
     * @param controllerState
     * @param counter
     * @private
     */
    private updateFrontendAclIds(serviceValues: L7VirtualServiceModel, controllerState: any, counter: number): void {
        const successAcls: any = this.utilsService.getObjectValue(controllerState[WizardTitleEnum.SUCCESS], `${[WizardTitleEnum.ACLS]}`);
        const frontends: any = this.utilsService.getObjectValue(controllerState, `${[WizardTitleEnum.FRONTENDS]}`);

        if (successAcls?.length) {
            if (serviceValues && !serviceValues.aclIds) {
                serviceValues.aclIds = [];
            }

            const matches: string[] = this.matchTempIdsToSuccessIds(serviceValues, successAcls);
            if (matches?.length) {
                serviceValues.aclIds = matches;
            } else if (successAcls?.length) {
                if (frontends.length === 1) {
                    this.updateAclIds(serviceValues, successAcls);
                } else if (successAcls.length >= 1) {
                    serviceValues.aclIds.push(this.fallBackToIndexes(successAcls, counter));
                } else {
                    serviceValues.aclIds = [this.fallBackToIndexes(successAcls, counter)];
                }
            } else {
                this.logService.error('Layer47SubmissionService: updateFrontendACLIds', 'No ACL ids found');
            }
        }
    }

    /**
     * Update service data with acl ids.
     * @param serviceValues
     * @param successAcls
     * @private
     */
    private updateAclIds(serviceValues: any, successAcls: any[]): void {
        for (const acl of successAcls) {
            serviceValues.aclIds.push(acl.id);
        }
    }

    /**
     * Match temp ids to success ids.
     * @param serviceValues
     * @param successAcls
     * @private
     */
    private matchTempIdsToSuccessIds(serviceValues: L7VirtualServiceModel, successAcls: any[]): string[] {
        const aclIds: string[] = serviceValues.aclIds;
        const newAclIds: any[] = [];
        for (const aclId of aclIds) {
            const match: any = successAcls.find((item: any): boolean => item.tempId === aclId);
            if (match?.id) {
                newAclIds.push(match.id);
            }
        }

        return newAclIds;
    }

    /** Creates an POST API call based on service name and state.
     * @param serviceName
     * @param serviceFormData
     * @private
     */
    private updateServiceApiCall(serviceName: string, serviceFormData: any): Observable<any> {
        switch (serviceName) {
            case WizardTitleEnum.FRONTENDS:
            case WizardTitleEnum.MONITORS:
                const frontend: L4FrontendServiceModel | L7VirtualServiceModel = serviceFormData;

                return this.apiService.updateFrontend(frontend.id, frontend);
            case WizardTitleEnum.BACKENDS:
                const backend: L4BackendServiceModel | L7BackendServiceModel = serviceFormData;

                return this.apiService.updateBackend(backend.id, backend);
            case WizardTitleEnum.REALSERVERS:
                const real: L4RealServiceModel | L7RealServiceModel = serviceFormData;

                return this.apiService.updateRealServer(real.id, real);
            case WizardTitleEnum.ACLS:
                const acl: AclModel = <AclModel>serviceFormData;

                return this.apiService.updateAclRule(acl.id, acl);
        }
    }

    /** Creates an POST API call based on service name and state.
     * @param serviceName
     * @param serviceFormData
     * @private
     */
    private createServiceApiCall(serviceName: string, serviceFormData: any): Observable<any> {
        delete serviceFormData.id; // when creating a service we shouldn't be sending an ID, however it is needed for
                                   // reference so this is the only place it can be deleted.
        switch (serviceName) {
            case WizardTitleEnum.FRONTENDS:
            case WizardTitleEnum.MONITORS:
                const frontend: L4FrontendServiceModel | L7VirtualServiceModel =
                    <L4FrontendServiceModel | L7VirtualServiceModel>serviceFormData;

                return this.apiService.addFrontend(frontend);
            case WizardTitleEnum.BACKENDS:
                const backend: L4BackendServiceModel | L7BackendServiceModel =
                    <L4BackendServiceModel | L7BackendServiceModel>serviceFormData;

                return this.apiService.addBackend(backend);
            case WizardTitleEnum.REALSERVERS:
                const real: L4RealServiceModel | L7RealServiceModel = <L4RealServiceModel | L7RealServiceModel>serviceFormData;

                return this.apiService.addRealServer(real);
            case WizardTitleEnum.ACLS:
                const acl: AclModel = <AclModel>serviceFormData;

                return this.apiService.addAclRule(acl);
        }
    }
}
